import React, { useRef, useState } from "react";
import styled from "styled-components/macro";
import * as Sentry from "@sentry/react";
import { TextQuestionWithButton } from "../TextQuestion";
import Question from "../Question";
import Label from "../Label";
import Input from "../Input";
import LinkButton from "../LinkButton";

const Select = styled(Input).attrs({ as: "select" })`
  appearance: auto;
`;

const Line = styled.p`
  margin: 0 0 1.5em;
`;

// A full UK postcode without its space.
const POSTCODE = /^[A-Z]{1,2}[0-9][A-Z0-9]?[0-9][A-Z]{2}$/;

const summary = (a) => [a.addressLine1, a.addressLine2, a.addressLine3, a.addressTown].filter(Boolean).join(", ");

// Wraps a form's address fields (the children) in a postcode lookup, which
// the backend switches on once it has an Ideal Postcodes key. The fields stay
// hidden until an address is chosen, the client asks to type it, or the step
// has errors; the lookup stays above them so a wrong pick can be re-picked.
function PostcodeLookup({ address, errors, onAddressSelected, children }) {
  const [manual, setManual] = useState(false);
  const [fieldsShown, setFieldsShown] = useState(false);
  const [postcode, setPostcode] = useState("");
  const [addresses, setAddresses] = useState(null);
  const [error, setError] = useState(null);
  const [searching, setSearching] = useState(false);
  const [failed, setFailed] = useState(false);
  // Bumped by every search, by "enter manually" and by editing the postcode, so a late
  // response can tell it's stale.
  const latest = useRef(0);
  const compact = postcode.replace(/\s+/g, "").toUpperCase();

  const overseas = Boolean(address.addressCountryCode) && address.addressCountryCode !== "GBR";
  const lookupShown = window.appConfig?.address_lookup_enabled === true && !manual && !overseas;
  if (!fieldsShown && (address.addressLine1 || errors.addressLine1 || errors.addressTown || errors.addressPostcode)) {
    setFieldsShown(true);
  }

  const choose = (chosen) => onAddressSelected({ ...chosen, addressCounty: "" });

  // Typing it in by hand keeps the postcode they've already given us.
  const enterManually = () => {
    latest.current += 1;
    if (POSTCODE.test(compact)) onAddressSelected({ addressPostcode: `${compact.slice(0, -3)} ${compact.slice(-3)}` });
    setManual(true);
  };

  const findAddresses = async (event) => {
    event.preventDefault();
    if (searching) return;
    setAddresses(null);
    if (!POSTCODE.test(compact)) {
      setError("Enter a real postcode, like SW1A 1AA");
      return;
    }
    const search = (latest.current += 1);
    setError(null);
    setSearching(true);
    try {
      const response = await fetch(`/api/address-lookup?postcode=${compact}`);
      if (!response.ok) throw new Error(`address lookup returned ${response.status}`);
      const body = await response.json();
      if (search !== latest.current) return;
      if (body.addresses.length === 0) setError("We couldn’t find any addresses for that postcode");
      else if (body.addresses.length === 1) choose(body.addresses[0]);
      else setAddresses(body.addresses);
    } catch (err) {
      if (search !== latest.current) return;
      Sentry.captureException(err, { tags: { action: "AddressLookup" } });
      setFailed(true);
      enterManually();
    } finally {
      if (search === latest.current) setSearching(false);
    }
  };

  return (
    <>
      {lookupShown && (
        <>
          <TextQuestionWithButton
            question="Find the address by postcode"
            name="postcodeLookup"
            size="small"
            autoComplete="postal-code"
            autoCapitalize="characters"
            value={postcode}
            valueEntered={(value) => {
              latest.current += 1;
              setSearching(false);
              setAddresses(null);
              setError(null);
              setPostcode(value);
            }}
            onKeyDown={(event) => event.key === "Enter" && findAddresses(event)}
            validationError={error}
            buttonText={searching ? "Finding…" : "Find address"}
            buttonDisabled={searching}
            buttonAction={findAddresses}
          />
          {addresses && (
            <Question>
              <Label htmlFor="select-address">Select the address</Label>
              <Select
                id="select-address"
                data-testid="select-address"
                autoFocus
                defaultValue=""
                onChange={(event) => choose(addresses[Number(event.target.value)])}
              >
                <option value="" disabled>
                  {addresses.length} addresses found
                </option>
                {addresses.map((option, index) => (
                  <option key={index} value={index}>
                    {summary(option)}
                  </option>
                ))}
              </Select>
            </Question>
          )}
          {!fieldsShown && (
            <Line>
              <LinkButton type="button" onClick={enterManually} data-testid="enter-address-manually">
                {addresses ? "The address isn’t listed" : "Enter the address manually"}
              </LinkButton>
            </Line>
          )}
        </>
      )}
      {failed && !lookupShown && <Line>We couldn’t look up addresses just now, so please type it in.</Line>}
      {(!lookupShown || fieldsShown) && children}
    </>
  );
}

export default PostcodeLookup;
